import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Globe, Server, Network, ListTree } from "lucide-react";
import api from "lib/api";

// Badge por tipo de registro DNS.
const TYPE_COLORS = {
  A: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  AAAA: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  CNAME: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  MX: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  NS: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  TXT: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  SOA: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  PTR: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
};

function TypeBadge({ t }) {
  const cls = TYPE_COLORS[t] || "bg-muted/40 text-muted-foreground border-border";
  return <span className={`inline-block rounded border px-1.5 py-0.5 text-xs font-medium ${cls}`}>{t || "—"}</span>;
}

// Cores por tipo de flag de enriquecimento.
const FLAG_COLORS = {
  datacenter: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  cloud: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  saas: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  dc: "bg-red-500/15 text-red-300 border-red-500/30",
  gc: "bg-red-500/15 text-red-300 border-red-500/30",
};

// flag clicável (cloud/saas/datacenter) -> busca; dc/gc não têm dork -> estático.
const FLAG_DORK_FIELD = { cloud: "cloud_product", saas: "saas_product", datacenter: "datacenter" };
function FlagChip({ f, onClick }) {
  const cls = FLAG_COLORS[f.kind] || "bg-muted/40 text-muted-foreground border-border";
  const base = `inline-block rounded border px-1.5 py-0.5 text-xs font-medium ${cls}`;
  if (onClick) {
    return <button type="button" onClick={onClick} className={base + " cursor-pointer hover:brightness-125"}>{f.label}</button>;
  }
  return <span className={base}>{f.label}</span>;
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
      <Icon className="h-6 w-6 text-primary" />
      <div>
        <div className="text-2xl font-semibold">{(value ?? 0).toLocaleString("pt-BR")}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

const isIp = (v) => typeof v === "string" && (/^\d{1,3}(\.\d{1,3}){3}$/.test(v) || v.includes(":"));
// parece um hostname/domínio (p/ tornar o valor de CNAME/NS/MX clicável).
const isDomainish = (v) => typeof v === "string" && /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9-]+)+\.?$/i.test(v.trim());

// link de busca com a dork/valor.
const searchTo = (q) => `/search?q=${encodeURIComponent(q)}`;
const dork = (field, val) => `${field}:${/\s/.test(String(val)) ? `"${val}"` : val}`;
const normFqdn = (s) => String(s || "").trim().toLowerCase().replace(/\.$/, "");

export default function DomainDetail() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/domain/${domain}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Falha ao carregar domínio."));
  }, [domain]);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  const records = data.records || [];

  return (
    <div className="w-full space-y-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ChevronLeft className="h-4 w-4" /> Resultados
      </button>

      <div className="flex items-center gap-2">
        <Globe className="h-6 w-6 text-emerald-400/80" />
        <h1 className="font-mono text-2xl font-semibold">{data.domain}</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat icon={ListTree} label="Subdomínios" value={data.subdomain_count} />
        <Stat icon={Network} label="Registros DNS" value={data.record_count} />
        <Stat icon={Server} label="IPs" value={data.ip_count} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        {/* Tabela de registros DNS */}
        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2 text-sm font-bold">Registros DNS</div>
          {records.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Nenhum registro.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Hostname</th>
                    <th className="px-4 py-2 font-medium">Tipo</th>
                    <th className="px-4 py-2 font-medium">Valor</th>
                    <th className="px-4 py-2 font-medium">Flags</th>
                    <th className="px-4 py-2 font-medium">Última vez</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i} className="border-b border-border/40 last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2 font-mono text-xs">
                        {r.fqdn
                          ? <Link to={searchTo(normFqdn(r.fqdn))} className="hover:underline">{r.fqdn}</Link>
                          : "—"}
                      </td>
                      <td className="px-4 py-2"><TypeBadge t={r.record_type} /></td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {isIp(r.value) ? (
                          <Link to={searchTo(r.value)} className="hover:underline">{r.value}</Link>
                        ) : isDomainish(r.value) ? (
                          <Link to={searchTo(normFqdn(r.value))} className="break-all hover:underline">{r.value}</Link>
                        ) : (
                          <span className="break-all">{r.value || "—"}</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {r.flags?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {r.flags.map((f, j) => {
                              const field = FLAG_DORK_FIELD[f.kind];
                              return (
                                <FlagChip
                                  key={j}
                                  f={f}
                                  onClick={field ? () => navigate(searchTo(dork(field, f.label))) : undefined}
                                />
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                        {r.observed_at ? String(r.observed_at).slice(0, 10) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Lateral: subdomínios + IPs */}
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-2 text-sm font-bold">Subdomínios ({data.subdomain_count || 0})</div>
            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {(data.subdomains || []).map((s) => (
                <Link key={s} to={searchTo(normFqdn(s))} className="truncate font-mono text-xs hover:underline">{s}</Link>
              ))}
              {!data.subdomains?.length && <span className="text-sm text-muted-foreground">—</span>}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-2 text-sm font-bold">IPs ({data.ip_count || 0})</div>
            <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
              {(data.ips || []).map((ip) => (
                <Link key={ip} to={searchTo(ip)} className="flex items-center gap-1.5 font-mono text-xs hover:underline">
                  <Server className="h-3 w-3" /> {ip}
                </Link>
              ))}
              {!data.ips?.length && <span className="text-sm text-muted-foreground">—</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
